import { defaultErrorHandler as commonErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

const NOT_FOUND = { status: 'not_found', value: null };
const ERROR = { status: 'error', value: null };

/**
 * Normalizes a configurations response into a `Map<key, value>` of the keys the
 * backend actually returned. Handles both shapes:
 * - new envelope: `{ configurations: [{ key, value }, ...] }`
 * - legacy single-object: `{ key?, value }` (only ever describes one key)
 *
 * A key the backend omits (not seeded in this env) is simply absent from the map.
 *
 * @param {unknown} data
 * @param {string[]} requestedKeys
 * @returns {Map<string, unknown>}
 */
function toValueMap(data, requestedKeys) {
  if (Array.isArray(data?.configurations)) {
    return new Map(data.configurations.map((item) => [item.key, item.value ?? null]));
  }
  // Legacy single-key body: it describes the one key we asked for.
  if (data && requestedKeys.length === 1) {
    return new Map([[requestedKeys[0], data.value ?? null]]);
  }
  return new Map();
}

export function createConfigurationsService(config = {}) {
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Configurations service', error));

  /**
   * Resolves a response into a per-key result map, one entry for every requested
   * key: `{ status: 'ok' | 'not_found' | 'error', value }`. A key omitted by the
   * backend resolves to `not_found`, leaving the default/off decision to callers.
   */
  const requestConfigurations = async (url, keys) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: createHeaders(),
      });

      // A blanket 404 (bad endpoint, not a missing key) maps every requested key
      // to not_found without surfacing an error, preserving the prior behaviour.
      if (response.status === 404) {
        return keys.reduce((acc, key) => ({ ...acc, [key]: NOT_FOUND }), {});
      }

      if (!response.ok) {
        throw new Error(`Failed to get configurations [${keys.join(', ')}]: ${response.status}`);
      }

      const valueByKey = toValueMap(await response.json(), keys);
      return keys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: valueByKey.has(key) ? { status: 'ok', value: valueByKey.get(key) } : NOT_FOUND,
        }),
        {}
      );
    } catch (error) {
      defaultErrorHandler(error);
      return keys.reduce((acc, key) => ({ ...acc, [key]: ERROR }), {});
    }
  };

  /**
   * Fetches several configurations in a single request.
   * @param {string[]} keys
   * @returns {Promise<Record<string, { status: string, value: unknown }>>}
   */
  const getConfigurations = (keys) => requestConfigurations(getApiUrls().configurations.getByKeys(keys), keys);

  /**
   * Single-key convenience wrapper over the same request/parse path.
   * @param {string} key
   * @returns {Promise<{ status: string, value: unknown }>}
   */
  const getConfiguration = async (key) => (await requestConfigurations(getApiUrls().configurations.getByKey(key), [key]))[key];

  return { getConfiguration, getConfigurations };
}

export default createConfigurationsService();
